<template>
  <teleport to="body">
    <v-navigation-drawer
      :model-value="modelValue"
      location="right"
      temporary
      :width="drawerWidth"
      class="ncrm-record-edit-drawer"
      role="dialog"
      :aria-modal="modelValue ? 'true' : 'false'"
      :aria-labelledby="titleId"
      @update:model-value="handleModelUpdate"
      @keydown.esc="handleEscape"
    >
      <div ref="layout" class="d-flex flex-column fill-height ncrm-record-edit-drawer__layout" @keydown.tab="trapFocus">
        <header class="ncrm-record-edit-drawer__header">
          <div class="ncrm-record-edit-drawer__header-row">
            <div v-if="headerIcon" class="ncrm-record-edit-drawer__icon">
              <v-icon :color="headerIconColor" size="20">{{ headerIcon }}</v-icon>
            </div>
            <div class="ncrm-record-edit-drawer__heading">
              <div :id="titleId" class="ncrm-record-edit-drawer__title">{{ title }}</div>
              <div v-if="subtitle" class="ncrm-record-edit-drawer__subtitle">
                {{ subtitle }}
              </div>
            </div>
            <button
              type="button"
              class="ncrm-record-edit-drawer__close"
              :disabled="closeDisabled"
              :aria-label="$t('app.actions.cancel')"
              @click="closeDialog('close')"
            >
              <v-icon size="16">mdi-close</v-icon>
            </button>
          </div>
        </header>

        <div ref="body" class="flex-grow-1 overflow-y-auto ncrm-record-edit-drawer__body">
          <slot />
        </div>

        <footer class="ncrm-record-edit-drawer__footer">
          <v-btn
            v-if="!hideCancel"
            variant="tonal"
            color="neutral"
            size="default"
            :disabled="cancelDisabled"
            class="ncrm-record-edit-drawer__cancel"
            @click="closeDialog('cancel')"
          >
            {{ $t("app.actions.cancel") }}
          </v-btn>
          <div class="d-flex align-center ga-2 ms-auto">
            <v-btn
              v-if="extraActionLabel"
              variant="text"
              :color="extraActionColor"
              :disabled="extraActionDisabled || loading"
              @click="$emit('extra-action')"
            >
              {{ extraActionLabel }}
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              size="default"
              :loading="loading"
              :disabled="saveDisabled"
              class="ncrm-record-edit-drawer__save"
              @click="$emit('save')"
            >
              {{ resolvedSaveLabel }}
            </v-btn>
          </div>
        </footer>
      </div>
    </v-navigation-drawer>
  </teleport>
</template>

<script>
import { focusFirstIn, rememberOpener, restoreOpener, trapDrawerFocus } from "@/Utile/drawerFocusTrap.mjs";

// Câmp Vuetify cu meniul deschis: activatorul (`aria-expanded`) sau rădăcina câmpului (`--active-menu`).
const MENU_OPEN_SELECTOR =
  '[aria-expanded="true"], .v-select--active-menu, .v-autocomplete--active-menu, .v-combobox--active-menu';

let activeScrollLocks = 0;

const toggleDocumentScrollLock = (shouldLock) => {
  if (typeof document === "undefined") return;

  if (shouldLock) {
    activeScrollLocks += 1;
    document.documentElement.classList.add("ncrm-record-edit-drawer-scroll-lock");
    document.body.classList.add("ncrm-record-edit-drawer-scroll-lock");
    return;
  }

  activeScrollLocks = Math.max(0, activeScrollLocks - 1);
  if (activeScrollLocks === 0) {
    document.documentElement.classList.remove("ncrm-record-edit-drawer-scroll-lock");
    document.body.classList.remove("ncrm-record-edit-drawer-scroll-lock");
  }
};

export default {
  name: "NcrmRecordEditDialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: [Number, String],
      default: 760,
    },
    extraActionLabel: {
      type: String,
      default: "",
    },
    extraActionColor: {
      type: String,
      default: "primary",
    },
    extraActionDisabled: {
      type: Boolean,
      default: false,
    },
    saveDisabled: {
      type: Boolean,
      default: false,
    },
    saveLabel: {
      type: String,
      default: "",
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    closeDisabled: {
      type: Boolean,
      default: false,
    },
    cancelDisabled: {
      type: Boolean,
      default: false,
    },
    // Fără „Anulează”: pentru drawerele cu salvare automată, unde singura acțiune este „Gata”.
    hideCancel: {
      type: Boolean,
      default: false,
    },
    headerIcon: {
      type: String,
      default: "",
    },
    headerIconColor: {
      type: String,
      default: "tertiary",
    },
  },
  data() {
    return { opener: null };
  },
  computed: {
    // Modal drawer (ui-kit.md → Drawer de formular): `role="dialog"` etichetat de titlu; un id pe instanță, drawerele pot fi imbricate.
    titleId() {
      return `ncrm-record-edit-title-${this.$.uid}`;
    },
    drawerWidth() {
      const normalized = String(this.maxWidth ?? "").trim();
      return normalized || 760;
    },
    resolvedSaveLabel() {
      const explicit = String(this.saveLabel || "").trim();
      if (explicit) return explicit;
      return this.$t("app.actions.save");
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(isOpen) {
        toggleDocumentScrollLock(Boolean(isOpen));
        if (isOpen) {
          // Elementul care are focusul ACUM l-a deschis (drawerul nu a preluat încă focusul): i se întoarce la închidere.
          this.opener = rememberOpener();
          this.$nextTick(() => this.focusDrawer());
        } else if (this.opener) {
          const opener = this.opener;
          this.opener = null;
          this.$nextTick(() => restoreOpener(opener));
        }
      },
    },
  },
  emits: ["update:modelValue", "save", "extra-action"],
  beforeUnmount() {
    if (this.modelValue) {
      toggleDocumentScrollLock(false);
    }
  },
  methods: {
    trapFocus: trapDrawerFocus,
    // Focus la deschidere: primul câmp din corp, altfel prima țintă din drawer (butonul de închidere).
    focusDrawer() {
      if (typeof window === "undefined") return;
      window.requestAnimationFrame(() => {
        if (!this.modelValue) return;
        focusFirstIn(this.$refs.body) || focusFirstIn(this.$refs.layout);
      });
    },
    // Escape închide drawerul, dar nu și când este al unui câmp cu meniul deschis (select / autocomplete / combobox: Escape închide meniul) sau a fost deja tratat.
    // Ținta este `<input>`-ul, iar Vuetify pune `aria-expanded` (VMenu) pe elementul-activator al câmpului și clasa `--active-menu` pe rădăcina lui:
    // se caută în sus, dar numai în interiorul drawerului.
    handleEscape(event) {
      if (event?.defaultPrevented) return;
      const openField = event?.target?.closest?.(MENU_OPEN_SELECTOR);
      if (openField && event.currentTarget?.contains?.(openField)) return;
      this.closeDialog("close");
    },
    closeDialog(source = "close") {
      if (this.persistent) return;
      if (source === "cancel" && this.cancelDisabled) return;
      if (source !== "cancel" && this.closeDisabled) return;
      this.$emit("update:modelValue", false);
    },
    handleModelUpdate(value) {
      const normalized = Boolean(value);
      if (!normalized && this.persistent) return;
      this.$emit("update:modelValue", normalized);
    },
  },
};
</script>

<style>
:root.ncrm-record-edit-drawer-scroll-lock,
body.ncrm-record-edit-drawer-scroll-lock {
  overflow: hidden !important;
}

.ncrm-record-edit-drawer.v-navigation-drawer {
  position: fixed !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  max-width: 100vw;
  z-index: 2600 !important;
}

.ncrm-record-edit-drawer .v-navigation-drawer__content {
  height: 100%;
  overflow: hidden;
}

.ncrm-record-edit-drawer__layout {
  min-height: 0;
}

.ncrm-record-edit-drawer.v-navigation-drawer--temporary.v-navigation-drawer--active + .v-navigation-drawer__scrim,
.ncrm-record-edit-drawer.v-navigation-drawer--temporary.v-navigation-drawer--active ~ .v-navigation-drawer__scrim {
  position: fixed !important;
  z-index: 2599 !important;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

.ncrm-record-edit-drawer__header {
  flex-shrink: 0;
  padding: 20px 24px 18px;
  border-bottom: 1px solid var(--ncrm-color-border-default);
  background: linear-gradient(180deg, rgb(var(--v-theme-surface)) 0%, var(--app-warm-highlight-soft-bg) 100%);
}

.ncrm-record-edit-drawer__header-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.ncrm-record-edit-drawer__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(36, 200, 198, 0.12);
  border: 1px solid rgba(36, 200, 198, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ncrm-record-edit-drawer__heading {
  flex: 1;
  min-width: 0;
}

.ncrm-record-edit-drawer__title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  color: var(--ncrm-color-text-strong);
}

.ncrm-record-edit-drawer__subtitle {
  font-size: 12px;
  line-height: 1.4;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.6;
  margin-top: 4px;
}

.ncrm-record-edit-drawer__close {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: transparent;
  border: 1px solid var(--ncrm-color-border-default);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
}

.ncrm-record-edit-drawer__close:hover:not(:disabled) {
  background: var(--ncrm-color-hover-default);
  border-color: var(--ncrm-color-border-strong);
}

.ncrm-record-edit-drawer__close:focus-visible {
  outline: 2px solid var(--ncrm-color-primary-focus);
  outline-offset: 2px;
}

.ncrm-record-edit-drawer__close:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ncrm-record-edit-drawer__body {
  min-height: 0;
  overscroll-behavior: contain;
  padding: 24px;
}

.ncrm-record-edit-drawer__footer {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid var(--ncrm-color-border-default);
}
@media (max-width: 600px) { .ncrm-record-edit-drawer__footer { flex-wrap: wrap; } }
</style>
